import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { combineIngredients, groupByCategory, groupIngredientsByCategory, CATEGORY_ORDER } from '../utils/ingredientUtils';
import { useData } from '../contexts/DataContext';

// ─────────────────────────────────────────────────────────────────────────────
// INGREDIENT ROW
// ─────────────────────────────────────────────────────────────────────────────
function IngredientRow({ text, summed = false, checked = false, onToggle }) {
  return (
    <li onClick={onToggle} style={{
      display: 'flex', alignItems: 'flex-start', gap: '0.6rem',
      padding: '0.45rem 0.9rem', cursor: 'pointer',
      borderBottom: '1px solid #f5f5f5', userSelect: 'none',
      background: checked ? '#fafafa' : 'white', transition: 'background 0.1s',
    }}>
      <span style={{ fontSize: '1rem', color: '#494949', flexShrink: 0, marginTop: '1px' }}>
        {checked ? '☑' : '☐'}
      </span>
      <span style={{ fontSize: '0.92rem', lineHeight: 1.4, flex: 1, color: checked ? '#bbb' : '#313131', textDecoration: checked ? 'line-through' : 'none' }}>
        {text}
      </span>
      {summed && !checked && (
        <span style={{ fontSize: '0.68rem', color: '#E67E22', fontWeight: 700, background: '#FEF9F0', border: '1px solid #F5CBA7', borderRadius: '10px', padding: '0.1rem 0.45rem', flexShrink: 0, alignSelf: 'center' }}>combined</span>
      )}
    </li>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// NON-FOOD ROW
// ─────────────────────────────────────────────────────────────────────────────
function NonFoodRow({ item, checked, onToggle }) {
  return (
    <li onClick={onToggle} style={{
      display: 'flex', alignItems: 'center', gap: '0.6rem',
      padding: '0.45rem 0.9rem', cursor: 'pointer',
      borderBottom: '1px solid #f5f5f5', userSelect: 'none',
      background: checked ? '#fafafa' : 'white', transition: 'background 0.1s',
    }}>
      <span style={{ fontSize: '1rem', color: '#494949', flexShrink: 0 }}>{checked ? '☑' : '☐'}</span>
      <span style={{ fontSize: '0.92rem', lineHeight: 1.4, flex: 1, color: checked ? '#bbb' : '#313131', textDecoration: checked ? 'line-through' : 'none' }}>
        {item}
      </span>
    </li>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// HELPERS
// ─────────────────────────────────────────────────────────────────────────────
function extractIngredients(recipe, scale = 1) {
  const ing = recipe.extendedIngredients;
  if (!ing || ing.length === 0) return [];
  const strings = ing[0]?.section !== undefined
    ? ing.flatMap(s => s.ingredients.map(i => i.original))
    : ing.map(i => i.original);
  if (scale === 1) return strings;
  return strings.map(str => scaleIngredientString(str, scale));
}

function scaleIngredientString(original, factor) {
  if (!factor || factor === 1) return original;
  const fractionToDecimal = (s) => s.includes('/') ? s.split('/').reduce((a, b) => a / b) : parseFloat(s);
  const decimalToFraction = (d) => {
    if (d === 0) return '0';
    const tol = 0.02, common = [[1,8],[1,4],[1,3],[1,2],[2,3],[3,4],[7,8]];
    const w = Math.floor(d), r = d - w;
    if (r < tol) return w === 0 ? '0' : String(w);
    if (r > 1 - tol) return String(w + 1);
    let best = null, bestD = Infinity;
    for (const [n, dn] of common) { const diff = Math.abs(r - n / dn); if (diff < bestD) { bestD = diff; best = [n, dn]; } }
    if (best && bestD < tol) { const f = `${best[0]}/${best[1]}`; return w === 0 ? f : `${w} ${f}`; }
    return String(parseFloat(d.toFixed(2)));
  };
  const patterns = [
    [/^(\d+)\s+and\s+(\d+\/\d+)/, (m) => parseInt(m[1]) + fractionToDecimal(m[2])],
    [/^(\d+)\s+(\d+\/\d+)/,       (m) => parseInt(m[1]) + fractionToDecimal(m[2])],
    [/^(\d+\/\d+)/,                (m) => fractionToDecimal(m[1])],
    [/^(\d+(?:\.\d+)?)-(\d+(?:\.\d+)?)/, (m) => ({ lo: parseFloat(m[1]), hi: parseFloat(m[2]), range: true })],
    [/^(\d+(?:\.\d+)?)/,           (m) => parseFloat(m[1])],
  ];
  for (const [re, parse] of patterns) {
    const m = original.match(re);
    if (!m) continue;
    const val = parse(m);
    const rest = original.slice(m[0].length);
    if (val?.range) return `${decimalToFraction(val.lo * factor)}-${decimalToFraction(val.hi * factor)}${rest}`;
    if (['°', '%', '"', "'"].includes((rest[0] || ''))) return original;
    return decimalToFraction(val * factor) + rest;
  }
  return original;
}

function RecipeIngredientList({ recipe, scale = 1, checkedIngredients = {}, onToggleIngredient }) {
  const ing = recipe.extendedIngredients;
  if (!ing || ing.length === 0) return null;
  const allStrings = ing[0]?.section !== undefined
    ? ing.flatMap(s => s.ingredients.map(i => scaleIngredientString(i.original, scale)))
    : ing.map(i => scaleIngredientString(i.original, scale));
  const categoryGroups = groupIngredientsByCategory(allStrings);
  return (
    <div>
      {categoryGroups.map(({ category, emoji, items }) => (
        <div key={category}>
          <p style={{ margin: '0.5rem 0.9rem 0.2rem', fontSize: '0.72rem', fontWeight: 700, color: '#888', textTransform: 'uppercase', letterSpacing: '0.05em', display: 'flex', alignItems: 'center', gap: '4px' }}>
            <span>{emoji}</span>{category}
          </p>
          <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
            {items.map((item, ii) => (
              <IngredientRow
                key={ii} text={item}
                checked={!!checkedIngredients[item]}
                onToggle={() => onToggleIngredient(item)}
              />
            ))}
          </ul>
        </div>
      ))}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// NON-FOOD PANEL
// ─────────────────────────────────────────────────────────────────────────────
function NonFoodPanel({ checkedItems, onToggle, onClear }) {
  const { storeItems } = useData();
  const [isOpen, setIsOpen] = useState(false);
  const [collapsedCategories, setCollapsedCategories] = useState({});

  useEffect(() => {
    if (storeItems.length > 0) {
      setCollapsedCategories(Object.fromEntries(storeItems.map(({ category }) => [category, true])));
    }
  }, [storeItems]);

  const checkedCount = Object.values(checkedItems).filter(Boolean).length;
  const toggleCategory = (cat) => setCollapsedCategories(prev => ({ ...prev, [cat]: !prev[cat] }));

  return (
    <div style={{ borderTop: '2px solid #E67E22', background: '#fffbf5', flexShrink: 0 }}>
      <div
        onClick={() => setIsOpen(o => !o)}
        style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0.75rem 1.5rem', cursor: 'pointer', userSelect: 'none' }}
        onMouseEnter={(e) => e.currentTarget.style.background = '#fff3e0'}
        onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <span style={{ fontSize: '1rem' }}>🧴</span>
          <span style={{ fontWeight: 700, fontSize: '0.95rem', color: '#E67E22' }}>Non-Food Items</span>
          {checkedCount > 0 && (
            <span style={{ fontSize: '0.72rem', fontWeight: 700, color: '#E67E22', background: '#FEF9F0', border: '1px solid #F5CBA7', borderRadius: '10px', padding: '0.1rem 0.5rem' }}>
              {checkedCount} selected
            </span>
          )}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          {checkedCount > 0 && isOpen && (
            <button onClick={(e) => { e.stopPropagation(); onClear(); }} style={{ fontSize: '0.75rem', color: '#999', background: 'none', border: '1px solid #ddd', borderRadius: '4px', padding: '2px 8px', cursor: 'pointer' }}
              onMouseEnter={(e) => e.currentTarget.style.color = '#ff4444'}
              onMouseLeave={(e) => e.currentTarget.style.color = '#999'}
            >Clear All</button>
          )}
          <span style={{ fontSize: '0.85rem', color: '#E67E22' }}>{isOpen ? '▾' : '▸'}</span>
        </div>
      </div>
      {isOpen && (
        <div style={{ maxHeight: '320px', overflowY: 'auto', borderTop: '1px solid #f5deb3' }}>
          {storeItems.map(({ category, emoji, items }) => {
            const isCatCollapsed = collapsedCategories[category];
            const catCheckedCount = items.filter(item => checkedItems[item]).length;
            return (
              <div key={category} style={{ borderBottom: '1px solid #f5deb3' }}>
                <div onClick={() => toggleCategory(category)} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0.5rem 1.5rem', cursor: 'pointer', userSelect: 'none', background: '#fff8f0' }}
                  onMouseEnter={(e) => e.currentTarget.style.background = '#ffeedd'}
                  onMouseLeave={(e) => e.currentTarget.style.background = '#fff8f0'}
                >
                  <span style={{ fontWeight: 700, fontSize: '0.82rem', color: '#494949', display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <span>{emoji}</span>{category}
                    {catCheckedCount > 0 && (
                      <span style={{ fontSize: '0.68rem', fontWeight: 700, color: '#E67E22', background: '#FEF9F0', border: '1px solid #F5CBA7', borderRadius: '10px', padding: '0.1rem 0.4rem' }}>{catCheckedCount}</span>
                    )}
                  </span>
                  <span style={{ fontSize: '0.75rem', color: '#aaa' }}>{isCatCollapsed ? '▸' : '▾'}</span>
                </div>
                {!isCatCollapsed && (
                  <ul style={{ listStyle: 'none', margin: 0, padding: 0, background: 'white' }}>
                    {items.map(item => (
                      <NonFoodRow key={item} item={item} checked={!!checkedItems[item]} onToggle={() => onToggle(item)} />
                    ))}
                  </ul>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// GROCERY LIST MODAL
// ─────────────────────────────────────────────────────────────────────────────
function GroceryListModal({ recipes: recipesForList, onClose }) {
  const { storeItems } = useData();
  const [viewMode, setViewMode] = useState('grouped');
  const [collapsedCategories, setCollapsedCategories] = useState(
    () => Object.fromEntries(CATEGORY_ORDER.map(cat => [cat, true]))
  );
  const [collapsedRecipes, setCollapsedRecipes] = useState(
    () => Object.fromEntries(recipesForList.flatMap(({ day, sides }) => [
      [day, true],
      ...(sides || []).map(s => [`${day}-side-${s.id}`, true]),
    ]))
  );

  const [checkedNonFood, setCheckedNonFood] = useState({});
  const [checkedIngredients, setCheckedIngredients] = useState({});
  const handleToggleNonFood = (item) => setCheckedNonFood(prev => ({ ...prev, [item]: !prev[item] }));
  const handleClearNonFood  = () => setCheckedNonFood({});
  const handleToggleIngredient = (key) => setCheckedIngredients(prev => ({ ...prev, [key]: !prev[key] }));

  const toggleRecipe   = (key) => setCollapsedRecipes(prev => ({ ...prev, [key]: !prev[key] }));
  const toggleCategory = (cat) => setCollapsedCategories(prev => ({ ...prev, [cat]: !prev[cat] }));

  const totalRecipeCount = recipesForList.reduce((acc, { sides }) => acc + 1 + (sides?.length ?? 0), 0);

  const allRawIngredients = recipesForList.flatMap(({ recipe, sides, scale = 1 }) => [
    ...extractIngredients(recipe, scale),
    ...(sides ?? []).flatMap(s => s.recipe ? extractIngredients(s.recipe) : []),
  ]);
  const combinedIngredients = combineIngredients(allRawIngredients);
  const categoryGroups = groupByCategory(combinedIngredients);

  const handleBackdropClick = (e) => { if (e.target === e.currentTarget) onClose(); };
  useEffect(() => {
    const handleKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [onClose]);

  const selectedNonFoodByCategory = storeItems
    .map(({ category, emoji, items }) => ({
      category, emoji,
      items: items.filter(item => checkedNonFood[item]),
    }))
    .filter(({ items }) => items.length > 0);

  const printGroceryList = () => {
    const printWindow = window.open('', '', 'height=800,width=800');
    if (!printWindow) { alert('Please allow popups for this site to use the print feature.'); return; }

    const dateStr = new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
    const nonFoodCount = Object.values(checkedNonFood).filter(Boolean).length;

    printWindow.document.write('<html><head><title>Weekly Grocery List</title><style>');
    printWindow.document.write('body { font-family: Arial, sans-serif; padding: 20px; }');
    printWindow.document.write('h1 { color: #313131; border-bottom: 2px solid #494949; padding-bottom: 10px; }');
    printWindow.document.write('h2 { color: #494949; margin-top: 24px; margin-bottom: 6px; font-size: 15px; border-bottom: 1px solid #ddd; padding-bottom: 4px; }');
    printWindow.document.write('h2.nonfood { color: #E67E22; border-bottom-color: #F5CBA7; }');
    printWindow.document.write('.section-divider { border: none; border-top: 3px solid #313131; margin: 30px 0 20px; }');
    printWindow.document.write('h3 { color: #777; margin: 10px 0 2px; font-size: 13px; text-transform: uppercase; letter-spacing: 0.05em; }');
    printWindow.document.write('ul { list-style: none; padding: 0; margin: 0; }');
    printWindow.document.write('li { padding: 6px 0; border-bottom: 1px solid #eee; font-size: 14px; }');
    printWindow.document.write('li.checked { color: #bbb; text-decoration: line-through; }');
    printWindow.document.write('li.checked:before { content: "☑  "; font-size: 16px; }');
    printWindow.document.write('li:not(.checked):before { content: "☐  "; font-size: 16px; }');
    printWindow.document.write('.combined { color: #E67E22; font-style: italic; }');
    printWindow.document.write('.meta { color: #888; font-size: 13px; margin-bottom: 20px; }');
    printWindow.document.write('@media print { body { padding: 10px; } }');
    printWindow.document.write('</style></head><body>');
    printWindow.document.write('<h1>Weekly Grocery List</h1>');
    printWindow.document.write('<p class="meta">' + totalRecipeCount + ' recipe' + (totalRecipeCount !== 1 ? 's' : '') + ' &mdash; ' + dateStr + '</p>');

    if (viewMode === 'grouped') {
      recipesForList.forEach(({ day, recipe, sides, scale = 1 }) => {
        const scaledServings = Math.round(recipe.servings * scale * 10) / 10;
        const scaleNote = scale !== 1 ? ' (x' + parseFloat(scale.toFixed(2)) + ', ' + scaledServings + ' srv)' : ' (' + recipe.servings + ' srv)';
        printWindow.document.write('<h2>' + day + ' &mdash; ' + recipe.title + scaleNote + '</h2>');
        const ing = recipe.extendedIngredients || [];
        const strings = ing[0]?.section !== undefined
          ? ing.flatMap(s => (s.ingredients || []).map(i => scaleIngredientString(i.original, scale)))
          : ing.map(i => scaleIngredientString(i.original, scale));
        printWindow.document.write('<ul>');
        strings.forEach(i => {
          const isChecked = !!checkedIngredients[i];
          printWindow.document.write('<li' + (isChecked ? ' class="checked"' : '') + '>' + i + '</li>');
        });
        printWindow.document.write('</ul>');
        if (sides && sides.length > 0) {
          sides.forEach(side => {
            if (!side.recipe) return;
            printWindow.document.write('<h3>Side: ' + side.title + '</h3><ul>');
            const sing = side.recipe.extendedIngredients || [];
            const sstrings = sing[0]?.section !== undefined
              ? sing.flatMap(s => (s.ingredients || []).map(i => i.original))
              : sing.map(i => i.original);
            sstrings.forEach(i => {
              const isChecked = !!checkedIngredients[i];
              printWindow.document.write('<li' + (isChecked ? ' class="checked"' : '') + '>' + i + '</li>');
            });
            printWindow.document.write('</ul>');
          });
        }
      });
    } else {
      categoryGroups.forEach(({ category, emoji, items }) => {
        printWindow.document.write('<h2>' + emoji + ' ' + category + '</h2><ul>');
        items.forEach(({ display, summed }) => {
          const isChecked = !!checkedIngredients[display];
          const cls = [summed ? 'combined' : '', isChecked ? 'checked' : ''].filter(Boolean).join(' ');
          printWindow.document.write('<li' + (cls ? ' class="' + cls + '"' : '') + '>' + display + (summed ? ' (combined)' : '') + '</li>');
        });
        printWindow.document.write('</ul>');
      });
    }

    if (selectedNonFoodByCategory.length > 0) {
      printWindow.document.write('<hr class="section-divider">');
      printWindow.document.write('<h1>Non-Food Items (' + nonFoodCount + ' item' + (nonFoodCount !== 1 ? 's' : '') + ')</h1>');
      selectedNonFoodByCategory.forEach(({ category, emoji, items }) => {
        printWindow.document.write('<h2 class="nonfood">' + emoji + ' ' + category + '</h2><ul>');
        items.forEach(item => printWindow.document.write('<li>' + item + '</li>'));
        printWindow.document.write('</ul>');
      });
    }

    printWindow.document.write('</body></html>');
    printWindow.document.close();
    printWindow.print();
  };

  const toggleBtn = (active) => ({
    padding: '0.45rem 1.1rem',
    border: '2px solid ' + (active ? '#313131' : '#ddd'),
    borderRadius: '20px',
    background: active ? '#313131' : 'white',
    color: active ? 'white' : '#666',
    fontSize: '0.88rem', fontWeight: 600, cursor: 'pointer',
  });

  return (
    <div onClick={handleBackdropClick} style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)',
      zIndex: 3000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem',
    }}>
      <div style={{
        background: 'white', borderRadius: '12px', width: '100%', maxWidth: '620px',
        maxHeight: '90vh', display: 'flex', flexDirection: 'column',
        boxShadow: '0 20px 60px rgba(0,0,0,0.3)', overflow: 'hidden',
      }}>
        <div style={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
          padding: '1.25rem 1.5rem 1rem', borderBottom: '1px solid #eee', flexShrink: 0,
        }}>
          <div>
            <h2 style={{ margin: 0, fontSize: '1.3rem', fontWeight: 700, color: '#313131' }}>Weekly Grocery List</h2>
            <p style={{ margin: '0.2rem 0 0', fontSize: '0.85rem', color: '#888' }}>
              {totalRecipeCount} recipe{totalRecipeCount !== 1 ? 's' : ''} this week
            </p>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: '1.8rem', lineHeight: 1, cursor: 'pointer', color: '#999', padding: '0 0.25rem', marginTop: '-0.25rem' }}>x</button>
        </div>

        <div style={{ display: 'flex', gap: '0.5rem', padding: '0.85rem 1.5rem', borderBottom: '1px solid #eee', flexShrink: 0, background: '#fafafa' }}>
          <button style={toggleBtn(viewMode === 'grouped')} onClick={() => setViewMode('grouped')}>By Recipe</button>
          <button style={toggleBtn(viewMode === 'combined')} onClick={() => setViewMode('combined')}>Combined List</button>
        </div>

        <div style={{ overflowY: 'auto', flex: 1, padding: '0.75rem 1.5rem' }}>
          {viewMode === 'grouped' ? (
            recipesForList.map(({ day, recipe, sides, scale = 1 }) => {
              const scaledServings = Math.round(recipe.servings * scale * 10) / 10;
              const isCollapsed = collapsedRecipes[day];
              return (
                <div key={day} style={{ marginBottom: '1.25rem' }}>
                  <div style={{ borderRadius: '8px', border: '1px solid #eee', overflow: 'hidden', marginBottom: sides?.length ? '6px' : 0 }}>
                    <div onClick={() => toggleRecipe(day)} style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', padding: '0.65rem 0.9rem', background: '#f5f5f5', borderBottom: isCollapsed ? 'none' : '1px solid #eee', flexWrap: 'wrap', cursor: 'pointer', userSelect: 'none' }}>
                      <span style={{ background: '#313131', color: 'white', fontSize: '0.72rem', fontWeight: 700, padding: '0.15rem 0.55rem', borderRadius: '20px', textTransform: 'uppercase', letterSpacing: '0.04em', flexShrink: 0 }}>{day}</span>
                      <span style={{ fontWeight: 700, fontSize: '0.95rem', color: '#313131', flex: 1 }}>{recipe.title}</span>
                      <span style={{ fontSize: '0.75rem', color: scale !== 1 ? '#1565C0' : '#888', flexShrink: 0, fontWeight: scale !== 1 ? 700 : 400 }}>
                        {scaledServings} srv{scale !== 1 && ` (x${parseFloat(scale.toFixed(2))})`}
                      </span>
                      <span style={{ fontSize: '0.8rem', color: '#888', marginLeft: '4px' }}>{isCollapsed ? '▸' : '▾'}</span>
                    </div>
                    {!isCollapsed && <RecipeIngredientList recipe={recipe} scale={scale} checkedIngredients={checkedIngredients} onToggleIngredient={handleToggleIngredient} />}
                  </div>
                  {sides && sides.map((side) => {
                    const sideKey = `${day}-side-${side.id}`;
                    const isSideCollapsed = collapsedRecipes[sideKey];
                    return (
                      <div key={side.id} style={{ borderRadius: '8px', border: '1px dashed #d0e8d0', overflow: 'hidden', marginBottom: '6px', marginLeft: '12px' }}>
                        <div onClick={() => toggleRecipe(sideKey)} style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', padding: '0.55rem 0.9rem', background: '#f0faf0', borderBottom: isSideCollapsed ? 'none' : '1px solid #d0e8d0', flexWrap: 'wrap', cursor: 'pointer', userSelect: 'none' }}>
                          <span style={{ background: '#4CAF50', color: 'white', fontSize: '0.68rem', fontWeight: 700, padding: '0.1rem 0.5rem', borderRadius: '20px', textTransform: 'uppercase', letterSpacing: '0.04em', flexShrink: 0 }}>side</span>
                          <span style={{ fontWeight: 600, fontSize: '0.9rem', color: '#313131', flex: 1 }}>{side.title}</span>
                          <span style={{ fontSize: '0.8rem', color: '#888', marginLeft: '4px' }}>{isSideCollapsed ? '▸' : '▾'}</span>
                        </div>
                        {!isSideCollapsed && side.recipe && <RecipeIngredientList recipe={side.recipe} scale={1} checkedIngredients={checkedIngredients} onToggleIngredient={handleToggleIngredient} />}
                      </div>
                    );
                  })}
                </div>
              );
            })
          ) : (
            <div>
              {categoryGroups.map(({ category, emoji, items }) => {
                const isCollapsed = collapsedCategories[category];
                return (
                  <div key={category} style={{ marginBottom: '6px', borderRadius: '8px', border: '1px solid #eee', overflow: 'hidden' }}>
                    <div onClick={() => toggleCategory(category)} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0.6rem 0.9rem', background: '#f5f5f5', cursor: 'pointer', userSelect: 'none', borderBottom: isCollapsed ? 'none' : '1px solid #eee' }}
                      onMouseEnter={(e) => e.currentTarget.style.background = '#efefef'}
                      onMouseLeave={(e) => e.currentTarget.style.background = '#f5f5f5'}
                    >
                      <span style={{ fontWeight: 700, fontSize: '0.88rem', color: '#313131', display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <span>{emoji}</span>{category}
                        <span style={{ fontSize: '0.72rem', fontWeight: 600, color: '#888', background: 'white', border: '1px solid #ddd', borderRadius: '10px', padding: '0.1rem 0.5rem' }}>{items.length}</span>
                      </span>
                      <span style={{ fontSize: '0.8rem', color: '#aaa' }}>{isCollapsed ? '▸' : '▾'}</span>
                    </div>
                    {!isCollapsed && (
                      <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
                        {items.map(({ display, summed }, i) => (
                          <IngredientRow key={i} text={display} summed={summed} checked={!!checkedIngredients[display]} onToggle={() => handleToggleIngredient(display)} />
                        ))}
                      </ul>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <NonFoodPanel checkedItems={checkedNonFood} onToggle={handleToggleNonFood} onClear={handleClearNonFood} />

        <div style={{ display: 'flex', gap: '0.75rem', padding: '1rem 1.5rem', borderTop: '1px solid #eee', flexShrink: 0, background: '#fafafa' }}>
          <button onClick={printGroceryList} style={{ flex: 1, padding: '0.7rem', background: '#4CAF50', color: 'white', border: 'none', borderRadius: '6px', fontSize: '0.95rem', fontWeight: 700, cursor: 'pointer' }}
            onMouseEnter={(e) => e.currentTarget.style.background = '#45a049'}
            onMouseLeave={(e) => e.currentTarget.style.background = '#4CAF50'}
          >Print Grocery List</button>
          <button onClick={onClose} style={{ padding: '0.7rem 1.25rem', background: 'white', color: '#666', border: '1px solid #ddd', borderRadius: '6px', fontSize: '0.95rem', fontWeight: 600, cursor: 'pointer' }}
            onMouseEnter={(e) => e.currentTarget.style.background = '#f5f5f5'}
            onMouseLeave={(e) => e.currentTarget.style.background = 'white'}
          >Close</button>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// HELPERS
// ─────────────────────────────────────────────────────────────────────────────
function getMealId(dayValue) {
  if (!dayValue) return null;
  if (typeof dayValue === 'string') return dayValue;
  return dayValue.id ?? null;
}

function getMealSides(dayValue) {
  if (!dayValue || typeof dayValue === 'string') return [];
  return dayValue.sides ?? [];
}

function getMealScale(dayValue) {
  if (!dayValue || typeof dayValue === 'string') return 1;
  return dayValue.scale ?? 1;
}

// ─────────────────────────────────────────────────────────────────────────────
// MAIN COMPONENT
// ─────────────────────────────────────────────────────────────────────────────
function WeeklyPlanner() {
  const { weeklyMeals, persistMeals, recipes } = useData();

  const [isOpen,          setIsOpen]          = useState(false);
  const [showGroceryList, setShowGroceryList] = useState(false);
  const [sidesCollapsed,  setSidesCollapsed]  = useState({});
  const [sidesMenu,       setSidesMenu]       = useState(null);
  const [scaleEdit,       setScaleEdit]       = useState(null);
  const [dragFromDay,     setDragFromDay]     = useState(null);
  const [dragOverDay,     setDragOverDay]     = useState(null);

  const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

  // ── Drag handlers ──────────────────────────────────────────────────────────
  // Drag starts only from the handle — handle calls this with e.stopPropagation()
  const handleDragStart = (e, day) => {
    setDragFromDay(day);
    e.dataTransfer.effectAllowed = 'move';
  };
  const handleDragEnter = (e) => {
    e.preventDefault();
  };
  const handleDragOver = (e, day) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    // Use dragOver (fires continuously) instead of dragEnter (unreliable at speed)
    if (day !== dragFromDay) setDragOverDay(day);
  };
  const handleDragLeave = (e, day) => {
    if (!e.currentTarget.contains(e.relatedTarget)) {
      setDragOverDay(prev => prev === day ? null : prev);
    }
  };
  const handleDrop = (e, toDay) => {
    e.preventDefault();
    if (!dragFromDay || dragFromDay === toDay) { setDragFromDay(null); setDragOverDay(null); return; }
    persistMeals({ ...weeklyMeals, [toDay]: weeklyMeals[dragFromDay], [dragFromDay]: weeklyMeals[toDay] });
    setDragFromDay(null); setDragOverDay(null);
  };
  const handleDragEnd = () => { setDragFromDay(null); setDragOverDay(null); };

  // ── Meal actions ───────────────────────────────────────────────────────────
  const handleRemoveMeal = (day) => {
    const dayValue = weeklyMeals[day];
    const mealId   = getMealId(dayValue);
    const special  = getSpecialMeal(mealId);
    const recipe   = !special && mealId ? getRecipeById(mealId) : null;
    const mealName = special?.title || recipe?.title || 'this meal';
    if (window.confirm(`Remove "${mealName}" from ${day}?`)) {
      persistMeals({ ...weeklyMeals, [day]: null });
    }
  };

  const handleClearAll = () => {
    if (window.confirm('Are you sure you want to clear all meals from the weekly plan?')) {
      persistMeals({ Sunday: null, Monday: null, Tuesday: null, Wednesday: null, Thursday: null, Friday: null, Saturday: null });
    }
  };

  const toggleSides   = (day) => setSidesCollapsed(prev => ({ ...prev, [day]: !prev[day] }));
  const openSidesMenu = (e, day) => { e.stopPropagation(); setSidesMenu({ day, x: e.clientX, y: e.clientY }); };

  const handleAddSide = ({ id, title }) => {
    if (!sidesMenu) return;
    const { day } = sidesMenu;
    const dayValue = weeklyMeals[day];
    const sides = getMealSides(dayValue);
    if (sides.some(s => s.id === id)) { setSidesMenu(null); return; }
    const updatedDay = typeof dayValue === 'string'
      ? { id: dayValue, sides: [...sides, { id, title }] }
      : { ...dayValue, sides: [...sides, { id, title }] };
    persistMeals({ ...weeklyMeals, [day]: updatedDay });
    setSidesMenu(null);
  };

  const handleRemoveSide = (day, sideId) => {
    const dayValue = weeklyMeals[day];
    const sides = getMealSides(dayValue).filter(s => s.id !== sideId);
    const updatedDay = typeof dayValue === 'string' ? { id: dayValue, sides } : { ...dayValue, sides };
    persistMeals({ ...weeklyMeals, [day]: updatedDay });
  };

  const handleSaveScale = (day, targetServingsStr, baseServings) => {
    const target = parseFloat(targetServingsStr);
    if (!target || target <= 0 || !baseServings) { setScaleEdit(null); return; }
    const scale = parseFloat((target / baseServings).toFixed(6));
    const dayValue = weeklyMeals[day];
    const updatedDay = typeof dayValue === 'string' ? { id: dayValue, sides: [], scale } : { ...dayValue, scale };
    persistMeals({ ...weeklyMeals, [day]: updatedDay });
    setScaleEdit(null);
  };

  // ── Export / Import ────────────────────────────────────────────────────────
  const handleExport = () => {
    const today = new Date();
    const defaultName = `meal-plan-${today.toISOString().split('T')[0]}`;
    const customName = prompt('Enter a name for your meal plan:', defaultName);
    if (customName === null) return;
    const filename = (customName.trim() || defaultName) + '.json';
    const dataBlob = new Blob([JSON.stringify(weeklyMeals, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(dataBlob);
    const link = document.createElement('a');
    link.href = url; link.download = filename; link.click();
    URL.revokeObjectURL(url);
  };

  const handleImport = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const importedData = JSON.parse(event.target.result);
        const validDays = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
        if (validDays.every(day => day in importedData)) {
          persistMeals(importedData);
          alert('Meal plan imported successfully!');
        } else { alert('Invalid meal plan file format.'); }
      } catch (error) {
        alert('Error reading file. Please make sure it\'s a valid JSON file.');
      }
    };
    reader.readAsText(file);
    e.target.value = '';
  };

  // ── Print ──────────────────────────────────────────────────────────────────
  const printMealPlan = () => {
    const today = new Date();
    const defaultDate = today.toISOString().split('T')[0];
    const customDate = prompt('Enter the week start date (YYYY-MM-DD):', defaultDate);
    if (customDate === null) return;
    let selectedDate;
    try {
      if (customDate.trim()) {
        const [year, month, day] = customDate.split('-').map(Number);
        selectedDate = new Date(year, month - 1, day);
      } else { selectedDate = today; }
      if (isNaN(selectedDate.getTime())) selectedDate = today;
    } catch { selectedDate = today; }

    const dateStr = selectedDate.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
    const printWindow = window.open('', '', 'height=800,width=800');
    if (!printWindow) { alert('Popup blocked! Please allow popups for this site and try again.'); return; }

    printWindow.document.write('<html><head><title>Weekly Meal Plan</title><style>');
    printWindow.document.write('body { font-family: Arial, sans-serif; padding: 20px; }');
    printWindow.document.write('h1 { color: #313131; border-bottom: 2px solid #494949; padding-bottom: 10px; text-align: center; }');
    printWindow.document.write('.meal-grid { display: grid; grid-template-columns: repeat(7, 1fr); gap: 15px; margin-top: 20px; }');
    printWindow.document.write('.day-card { border: 2px solid #ccc; border-radius: 8px; padding: 10px; text-align: center; page-break-inside: avoid; }');
    printWindow.document.write('.day-name { font-weight: bold; font-size: 16px; margin-bottom: 10px; color: #494949; }');
    printWindow.document.write('.meal-image { width: 100%; border-radius: 8px; margin: 10px 0; }');
    printWindow.document.write('.meal-title { font-size: 12px; margin-top: 8px; line-height: 1.4; }');
    printWindow.document.write('.sides-list { font-size: 11px; color: #666; margin-top: 6px; text-align: left; }');
    printWindow.document.write('.no-meal { color: #999; font-style: italic; padding: 40px 0; }');
    printWindow.document.write('@media print { body { padding: 10px; } .meal-grid { grid-template-columns: repeat(7, 1fr); gap: 10px; } }');
    printWindow.document.write('</style></head><body>');
    printWindow.document.write('<h1>Weekly Meal Plan - Week of ' + dateStr + '</h1>');
    printWindow.document.write('<div class="meal-grid">');

    days.forEach(day => {
      const dayValue    = weeklyMeals[day];
      const mealId      = getMealId(dayValue);
      const sides       = getMealSides(dayValue);
      const specialMeal = mealId ? getSpecialMeal(mealId) : null;
      const recipe      = (!specialMeal && mealId) ? getRecipeById(mealId) : null;

      printWindow.document.write('<div class="day-card"><div class="day-name">' + day + '</div>');
      if (specialMeal) {
        printWindow.document.write('<img class="meal-image" src="' + specialMeal.image + '" alt="' + specialMeal.title + '">');
        printWindow.document.write('<div class="meal-title">' + specialMeal.title + '</div>');
      } else if (recipe) {
        printWindow.document.write('<img class="meal-image" src="' + recipe.image + '" alt="' + recipe.title + '">');
        printWindow.document.write('<div class="meal-title">' + recipe.title + '</div>');
        if (sides.length > 0) {
          printWindow.document.write('<div class="sides-list"><strong>Sides:</strong> ' + sides.map(s => s.title).join(', ') + '</div>');
        }
      } else {
        printWindow.document.write('<div class="no-meal">No meal planned</div>');
      }
      printWindow.document.write('</div>');
    });

    printWindow.document.write('</div></body></html>');
    printWindow.document.close();

    printWindow.onload = function () {
      const images = Array.from(printWindow.document.images);
      if (images.length === 0) { printWindow.print(); return; }
      let loaded = images.filter(img => img.complete).length;
      if (loaded === images.length) { printWindow.print(); return; }
      const check = () => { loaded++; if (loaded === images.length) printWindow.print(); };
      images.forEach(img => { if (!img.complete) { img.onload = check; img.onerror = check; } });
      setTimeout(() => printWindow.print(), 3000);
    };
  };

  // ── Lookups ────────────────────────────────────────────────────────────────
  const getRecipeById  = (id) => recipes.find(r => r.id === id || r.id === Number(id));
  const getSpecialMeal = (mealId) => {
    if (mealId === 'dine-out')   return { type: 'dine-out',   title: 'Dine Out',   image: '/images/dine-out.jpg' };
    if (mealId === 'delivery')   return { type: 'delivery',   title: 'Delivery',   image: '/images/delivery.jpg' };
    if (mealId === 'date-night') return { type: 'date-night', title: 'Date Night', image: '/images/date-night.jpg' };
    return null;
  };

  const recipesForGroceryList = days
    .filter(day => { const mealId = getMealId(weeklyMeals[day]); return mealId && !getSpecialMeal(mealId); })
    .map(day => {
      const dayValue = weeklyMeals[day];
      const mealId   = getMealId(dayValue);
      const scale    = getMealScale(dayValue);
      const recipe   = getRecipeById(mealId);
      const sides    = getMealSides(dayValue).map(s => ({ ...s, recipe: getRecipeById(s.id) })).filter(s => s.recipe);
      return { day, recipe, scale, sides };
    })
    .filter(({ recipe }) => recipe);

  // ── Render ─────────────────────────────────────────────────────────────────
  const plannedCount = days.filter(day => getMealId(weeklyMeals[day])).length;

  if (!isOpen) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', margin: '10px 0' }}>
        <button onClick={() => setIsOpen(true)} style={{ padding: '8px 20px', borderRadius: '20px', border: '1px solid #ddd', background: 'white', cursor: 'pointer', fontSize: '0.9rem', display: 'flex', alignItems: 'center', gap: '8px' }}>
          📅 Weekly Planner {plannedCount > 0 && <span style={{ background: '#E67E22', color: 'white', borderRadius: '10px', padding: '1px 7px', fontSize: '0.75rem', fontWeight: 700 }}>{plannedCount}/7</span>}
        </button>
      </div>
    );
  }

  return (
    <>
      {showGroceryList && recipesForGroceryList.length > 0 && (
        <GroceryListModal recipes={recipesForGroceryList} onClose={() => setShowGroceryList(false)} />
      )}

      <div style={{ background: 'white', border: '1px solid #eee', borderRadius: '12px', margin: '10px', padding: '1rem', boxShadow: '0 2px 12px rgba(0,0,0,0.06)' }}>
        {/* Planner header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
          <h2 style={{ margin: 0, fontSize: '1rem', fontWeight: 700 }}>📅 Weekly Meal Planner</h2>
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center' }}>
            {recipesForGroceryList.length > 0 && (
              <button onClick={() => setShowGroceryList(true)} style={{ fontSize: '0.8rem', padding: '4px 10px', border: '1px solid #ddd', borderRadius: '6px', background: 'white', cursor: 'pointer' }}>🛒 Grocery List</button>
            )}
            <button onClick={printMealPlan} style={{ fontSize: '0.8rem', padding: '4px 10px', border: '1px solid #ddd', borderRadius: '6px', background: 'white', cursor: 'pointer' }}>🖨 Print</button>
            <button onClick={handleExport}  style={{ fontSize: '0.8rem', padding: '4px 10px', border: '1px solid #ddd', borderRadius: '6px', background: 'white', cursor: 'pointer' }}>⬇ Export</button>
            <label style={{ fontSize: '0.8rem', padding: '4px 10px', border: '1px solid #ddd', borderRadius: '6px', background: 'white', cursor: 'pointer' }}>
              ⬆ Import
              <input type="file" accept=".json" onChange={handleImport} style={{ display: 'none' }} />
            </label>
            <button onClick={handleClearAll} style={{ fontSize: '0.8rem', padding: '4px 10px', border: '1px solid #ffcdd2', borderRadius: '6px', background: '#fff5f5', color: '#c62828', cursor: 'pointer' }}>🗑 Clear</button>
            <button onClick={() => setIsOpen(false)} style={{ fontSize: '0.8rem', padding: '4px 10px', border: '1px solid #ddd', borderRadius: '6px', background: 'white', cursor: 'pointer' }}>✕ Close</button>
          </div>
        </div>

        {/* Day columns */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '8px', overflowX: 'auto' }}>
          {days.map(day => {
            const dayValue    = weeklyMeals[day];
            const mealId      = getMealId(dayValue);
            const scale       = getMealScale(dayValue);
            const sides       = getMealSides(dayValue);
            const specialMeal = mealId ? getSpecialMeal(mealId) : null;
            const recipe      = (!specialMeal && mealId) ? getRecipeById(mealId) : null;
            const isDragOver  = dragOverDay === day;
            const isDragging  = dragFromDay === day;

            // Border color: orange if being dragged, green if drop target is empty,
            // blue if drop target already has a meal, default otherwise
            const borderColor = isDragging
              ? '#E67E22'
              : isDragOver
                ? (mealId ? '#2196F3' : '#4CAF50')
                : '#eee';
            const borderStyle = isDragOver ? '2px dashed' : isDragging ? '2px dashed' : '1px solid';

            return (
              <div
                key={day}
                // Drop target events on the whole card
                onDragEnter={handleDragEnter}
                onDragOver={(e) => handleDragOver(e, day)}
                onDragLeave={(e) => handleDragLeave(e, day)}
                onDrop={(e) => handleDrop(e, day)}
                onDragEnd={handleDragEnd}
                style={{
                  border: `${borderStyle} ${borderColor}`,
                  borderRadius: '10px',
                  padding: '8px',
                  minHeight: '120px',
                  background: isDragOver ? (mealId ? '#EBF5FB' : '#F0FBF0') : 'white',
                  transition: 'all 0.15s',
                  position: 'relative',
                }}
              >
                <div style={{ fontWeight: 700, fontSize: '0.78rem', color: '#494949', marginBottom: '6px', textAlign: 'center' }}>{day}</div>

                {specialMeal ? (
                  <div style={{ textAlign: 'center' }}>
                    <img src={specialMeal.image} alt={specialMeal.title} style={{ width: '100%', borderRadius: '8px', marginBottom: '4px' }} />
                    <div style={{ fontSize: '0.75rem', color: '#494949', fontWeight: 600 }}>{specialMeal.title}</div>
                    <button onClick={() => handleRemoveMeal(day)} style={{ marginTop: '4px', fontSize: '0.7rem', color: '#aaa', background: 'none', border: 'none', cursor: 'pointer' }}>remove</button>
                  </div>
                ) : recipe ? (
                  <div>
                    {/* ── Drag handle — only shown when a recipe is assigned ── */}
                    <div
                      draggable
                      onDragStart={(e) => { e.stopPropagation(); handleDragStart(e, day); }}
                      onDragEnd={handleDragEnd}
                      title="Drag to swap with another day"
                      style={{
                        position: 'absolute',
                        top: '6px',
                        left: '6px',
                        cursor: 'grab',
                        fontSize: '1.1rem',
                        color: '#ccc',
                        lineHeight: 1,
                        userSelect: 'none',
                        zIndex: 1,
                        padding: '2px 3px',
                        borderRadius: '4px',
                        transition: 'color 0.15s',
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.color = '#E67E22'}
                      onMouseLeave={(e) => e.currentTarget.style.color = '#ccc'}
                    >
                      ⠿
                    </div>

                    <Link to={`/recipe/${recipe.id}`} state={{ scaledServings: Math.round(recipe.servings * scale) }}>
                      <img src={recipe.image} alt={recipe.title} style={{ width: '100%', borderRadius: '8px', marginBottom: '4px' }} />
                    </Link>
                    <div style={{ fontSize: '0.75rem', color: '#313131', fontWeight: 600, lineHeight: 1.3 }}>{recipe.title}</div>

                    {/* Scale display */}
                    {scaleEdit?.day === day ? (
                      <form
                        onSubmit={(e) => { e.preventDefault(); handleSaveScale(day, scaleEdit.value, recipe.servings); }}
                        style={{ display: 'flex', gap: '4px', marginTop: '4px', alignItems: 'center' }}
                      >
                        <input
                          type="number" min="1" step="0.5" autoFocus
                          value={scaleEdit.value}
                          onChange={(e) => setScaleEdit(prev => ({ ...prev, value: e.target.value }))}
                          onKeyDown={(e) => { if (e.key === 'Escape') setScaleEdit(null); }}
                          style={{ width: '50px', fontSize: '0.75rem', padding: '2px 4px', border: '1px solid #2196F3', borderRadius: '4px', textAlign: 'center' }}
                        />
                        <span style={{ fontSize: '0.7rem', color: '#888', alignSelf: 'center' }}>srv</span>
                        <button type="submit" style={{ fontSize: '0.65rem', padding: '2px 5px', background: '#2196F3', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>✓</button>
                        <button type="button" onClick={() => setScaleEdit(null)} style={{ fontSize: '0.65rem', padding: '2px 5px', background: '#eee', color: '#666', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>✕</button>
                      </form>
                    ) : (
                      <div style={{ fontSize: '0.7rem', color: '#888', marginTop: '2px', display: 'flex', gap: '6px', alignItems: 'center' }}>
                        <button
                          onClick={() => setScaleEdit({ day, value: String(Math.round(recipe.servings * scale)) })}
                          title="Click to adjust servings"
                          style={{
                            display: 'flex', alignItems: 'center', gap: '3px',
                            background: scale !== 1 ? '#E3F2FD' : '#f5f5f5',
                            border: `1px solid ${scale !== 1 ? '#90CAF9' : '#ddd'}`,
                            borderRadius: '12px', padding: '2px 8px',
                            fontSize: '0.68rem', color: scale !== 1 ? '#1565C0' : '#888',
                            cursor: 'pointer', fontWeight: scale !== 1 ? 700 : 400,
                          }}
                        >
                          {Math.round(recipe.servings * scale)} srv
                          {scale !== 1 && <span style={{ fontSize: '0.62rem', opacity: 0.7 }}>× {parseFloat(scale.toFixed(2))}</span>}
                          <span style={{ fontSize: '0.62rem', opacity: 0.5 }}>✎</span>
                        </button>
                      </div>
                    )}

                    {/* Sides */}
                    {sides.length > 0 && (
                      <div style={{ marginTop: '4px' }}>
                        <button onClick={() => toggleSides(day)} style={{ fontSize: '0.68rem', color: '#E67E22', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>
                          {sidesCollapsed[day] ? `▸ ${sides.length} side${sides.length > 1 ? 's' : ''}` : '▾ sides'}
                        </button>
                        {!sidesCollapsed[day] && (
                          <ul style={{ listStyle: 'none', margin: '2px 0 0', padding: 0 }}>
                            {sides.map(side => {
                              const sideRecipe = getRecipeById(side.id);
                              return (
                                <li key={side.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '0.68rem', color: '#666', padding: '1px 0' }}>
                                  {sideRecipe ? <Link to={`/recipe/${sideRecipe.id}`} style={{ color: '#666', textDecoration: 'none' }}>{side.title}</Link> : <span>{side.title}</span>}
                                  <button onClick={() => handleRemoveSide(day, side.id)} style={{ background: 'none', border: 'none', color: '#bbb', cursor: 'pointer', fontSize: '0.7rem', padding: '0 2px' }}>✕</button>
                                </li>
                              );
                            })}
                          </ul>
                        )}
                      </div>
                    )}

                    <div style={{ display: 'flex', gap: '4px', marginTop: '4px', flexWrap: 'wrap' }}>
                      <button onClick={(e) => openSidesMenu(e, day)} style={{ fontSize: '0.68rem', color: '#888', background: 'none', border: '1px solid #ddd', borderRadius: '4px', padding: '1px 5px', cursor: 'pointer' }}>+ side</button>
                      <button onClick={() => handleRemoveMeal(day)} style={{ fontSize: '0.68rem', color: '#aaa', background: 'none', border: 'none', cursor: 'pointer' }}>remove</button>
                    </div>
                  </div>
                ) : (
                  <div style={{ textAlign: 'center', color: '#ccc', fontSize: '0.78rem', paddingTop: '20px' }}>
                    Right-click a recipe to add
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Sides quick-add menu */}
        {sidesMenu && (
          <div style={{ position: 'fixed', top: sidesMenu.y, left: sidesMenu.x, background: 'white', border: '1px solid #ddd', borderRadius: '8px', boxShadow: '0 4px 16px rgba(0,0,0,0.12)', zIndex: 2500, width: '220px', padding: '8px' }}>
            <input
              autoFocus
              type="text"
              placeholder="Search recipes…"
              onChange={(e) => {
                const q = e.target.value.toLowerCase();
                e.currentTarget.nextElementSibling.innerHTML = '';
                recipes
                  .filter(r => r.title.toLowerCase().includes(q))
                  .slice(0, 8)
                  .forEach(r => {
                    const div = document.createElement('div');
                    div.textContent = r.title;
                    div.style.cssText = 'padding:6px 8px;cursor:pointer;font-size:0.82rem;border-radius:4px';
                    div.onmouseenter = () => div.style.background = '#f5f5f5';
                    div.onmouseleave = () => div.style.background = 'white';
                    div.onclick = () => handleAddSide({ id: r.id, title: r.title });
                    e.currentTarget.nextElementSibling.appendChild(div);
                  });
              }}
              style={{ width: '100%', boxSizing: 'border-box', padding: '6px 8px', border: '1px solid #ddd', borderRadius: '6px', fontSize: '0.85rem', marginBottom: '6px' }}
            />
            <div style={{ maxHeight: '200px', overflowY: 'auto' }}>
              {recipes.slice(0, 8).map(r => (
                <div key={r.id} onClick={() => handleAddSide({ id: r.id, title: r.title })} style={{ padding: '6px 8px', cursor: 'pointer', fontSize: '0.82rem', borderRadius: '4px' }}
                  onMouseEnter={(e) => e.currentTarget.style.background = '#f5f5f5'}
                  onMouseLeave={(e) => e.currentTarget.style.background = 'white'}
                >
                  {r.title}
                </div>
              ))}
            </div>
            <button onClick={() => setSidesMenu(null)} style={{ width: '100%', marginTop: '6px', padding: '5px', border: '1px solid #eee', borderRadius: '6px', cursor: 'pointer', fontSize: '0.8rem', color: '#888', background: 'white' }}>Cancel</button>
          </div>
        )}
        {sidesMenu && <div style={{ position: 'fixed', inset: 0, zIndex: 2499 }} onClick={() => setSidesMenu(null)} />}
      </div>
    </>
  );
}

export default WeeklyPlanner;
